(() => {
  const SELECTORS = {
    icon: '#cart-icon-bubble',
    drawer: '#CartDrawer',
    cartItems: '#main-cart-items .js-contents',
    cartFooter: '#main-cart-footer .js-contents',
    notificationReward: '#cart-reward-progress',
    overlay: '#CartRewardsOverlay',
    overlayReward: '#CartRewardsOverlayLive'
  };

  let syncing = false;
  let queued = false;
  let discountAttemptFingerprint = '';

  const parseSection = (html, selector) => {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  };

  const replaceSection = (html, selector) => {
    const current = document.querySelector(selector);
    const fresh = parseSection(html, selector);
    if (current && fresh) current.replaceWith(fresh);
  };

  const refreshCartSurfaces = async () => {
    const names = ['cart-icon-bubble', 'cart-drawer', 'main-cart-items', 'main-cart-footer', 'cart-reward-progress'];
    const response = await fetch(`${routes.cart_url}?sections=${names.join(',')}`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Unable to refresh cart');
    const sections = await response.json();

    replaceSection(sections['cart-icon-bubble'], SELECTORS.icon);
    replaceSection(sections['cart-drawer'], SELECTORS.drawer);
    replaceSection(sections['main-cart-items'], SELECTORS.cartItems);
    replaceSection(sections['main-cart-footer'], SELECTORS.cartFooter);

    const rewardHost = document.querySelector(SELECTORS.notificationReward);
    const rewardSource = parseSection(sections['cart-reward-progress'], SELECTORS.notificationReward);
    if (rewardHost && rewardSource) rewardHost.innerHTML = rewardSource.innerHTML;

    const overlayReward = document.querySelector(SELECTORS.overlayReward);
    if (overlayReward && rewardSource) overlayReward.innerHTML = rewardSource.innerHTML;
  };

  const changeRewardLine = async (key, quantity) => {
    const response = await fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({ id: key, quantity })
    });
    if (!response.ok) throw new Error('Unable to update reward');
  };

  const addRewardLine = async (variantId) => {
    await fetch('/discount/FREESTICKERS50?redirect=/cart.js', { credentials: 'same-origin' });
    const response = await fetch(routes.cart_add_url, {
      ...fetchConfig('javascript'),
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1, properties: { _44_tints_reward: 'Free Sticker Pack' } }]
      })
    });
    if (!response.ok) throw new Error('Unable to add reward');
  };

  const reconcile = async (component) => {
    if (!component || syncing) {
      queued = true;
      return;
    }

    const subtotal = Number(component.dataset.rewardSubtotal || 0);
    const variantId = Number(component.dataset.stickerVariantId || 0);
    const rewardKey = component.dataset.rewardLineKey || '';
    const rewardQuantity = Number(component.dataset.rewardLineQuantity || 0);
    const rewardDiscountTotal = Number(component.dataset.rewardDiscountTotal || 0);
    const shouldHaveReward = subtotal >= 5000 && variantId > 0;
    const needsAdd = shouldHaveReward && !rewardKey;
    const needsRemove = !shouldHaveReward && Boolean(rewardKey);
    const discountFingerprint = `${subtotal}:${rewardKey}`;
    const needsDiscount = shouldHaveReward && Boolean(rewardKey) && rewardDiscountTotal <= 0 && discountAttemptFingerprint !== discountFingerprint;

    if (!needsAdd && !needsRemove && !needsDiscount) return;

    syncing = true;
    try {
      if (needsAdd) await addRewardLine(variantId);
      if (needsRemove) await changeRewardLine(rewardKey, Math.max(0, rewardQuantity - 1));
      if (needsDiscount) {
        discountAttemptFingerprint = discountFingerprint;
        await fetch('/discount/FREESTICKERS50?redirect=/cart.js', { credentials: 'same-origin' });
      }
      await new Promise((resolve) => window.setTimeout(resolve, 180));
      await refreshCartSurfaces();
    } catch (error) {
      console.error('[44 Tints cart rewards]', error);
    } finally {
      syncing = false;
      if (queued) {
        queued = false;
        const next = document.querySelector('[data-cart-reward-progress]');
        if (next) reconcile(next);
      }
    }
  };

  class CartRewardProgress extends HTMLElement {
    connectedCallback() {
      window.requestAnimationFrame(() => reconcile(this));
    }
  }

  if (!customElements.get('cart-reward-progress')) {
    customElements.define('cart-reward-progress', CartRewardProgress);
  }

  document.addEventListener('cart:refresh', () => refreshCartSurfaces().catch((error) => console.error('[44 Tints cart rewards]', error)));

  const setupRewardsOverlay = () => {
    const overlay = document.querySelector(SELECTORS.overlay);
    if (!overlay) return;

    const openers = document.querySelectorAll('[data-cart-rewards-open]');
    const closers = overlay.querySelectorAll('[data-cart-rewards-close]');

    const open = () => {
      refreshCartSurfaces().catch((error) => console.error('[44 Tints cart rewards]', error));
      openers.forEach((button) => button.classList.remove('is-reward-hovered'));
      document.documentElement.classList.add('cart-rewards-overlay-open');
      if (typeof overlay.showModal === 'function') overlay.showModal();
      else overlay.setAttribute('open', '');
    };

    const close = () => {
      if (typeof overlay.close === 'function') overlay.close();
      else overlay.removeAttribute('open');
    };

    openers.forEach((button) => {
      button.addEventListener('pointerenter', () => button.classList.add('is-reward-hovered'));
      button.addEventListener('pointerleave', () => button.classList.remove('is-reward-hovered'));
      button.addEventListener('focus', () => button.classList.add('is-reward-hovered'));
      button.addEventListener('blur', () => button.classList.remove('is-reward-hovered'));
      button.addEventListener('click', open);
    });
    closers.forEach((button) => button.addEventListener('click', close));
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) close();
    });
    overlay.addEventListener('close', () => document.documentElement.classList.remove('cart-rewards-overlay-open'));
    overlay.addEventListener('cancel', () => document.documentElement.classList.remove('cart-rewards-overlay-open'));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setupRewardsOverlay, { once: true });
  else setupRewardsOverlay();
})();

